import React, { Fragment, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { getRevenueReport } from "../../../actions/adminReportActions";
import { Bar } from "react-chartjs-2";
import CurrencyToggle from "../../Common/CurrencyToggle";
import { niceMax } from "../../../utils/chartPlugins";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);
import Loader from "../../Loader";
import { downloadCSV } from '../../../utils/exportCsv';
import { getPlanChartLabel } from '../../../utils/subscriptionPlanLabel';

const RevenueReport = () => {
  const dispatch = useDispatch();
  const { revenueReport, loading } = useSelector((state) => state.adminReport);

  const [dateRange, setDateRange] = useState('last30days');
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');
  const [currencyUnit, setCurrencyUnit] = useState(() => { try { return localStorage.getItem('reports_currency_unit') || 'NGN'; } catch(e){ return 'NGN'; } });

  useEffect(() => {
    if (dateRange === 'custom') {
      if (customStartDate && customEndDate) {
        dispatch(getRevenueReport('custom', customStartDate, customEndDate));
      }
    } else {
      dispatch(getRevenueReport(dateRange));
    }
  }, [dispatch, dateRange, customStartDate, customEndDate]);

  const revenueOverTime = revenueReport?.revenueOverTime || [];
  const revenueByPlan = revenueReport?.revenueByPlan || [];

  const revenueChartData = {
    labels: revenueOverTime.map((d) => d.date),
    datasets: [{ label: 'Revenue', data: revenueOverTime.map((d) => Number(d.revenue || 0) * (currencyUnit === 'KOBO' ? 100 : 1)), backgroundColor: '#3c8dbc' }]
  };

  const revenueByPlanData = {
    labels: revenueByPlan.map((p) => getPlanChartLabel(p)),
    datasets: [{ label: 'Revenue', data: revenueByPlan.map(p => Number(p.revenue || 0) * (currencyUnit === 'KOBO' ? 100 : 1)), backgroundColor: '#00a65a' }]
  };

  const planChartMax = niceMax(revenueByPlanData.datasets[0].data);

  const revenueByPlanChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: true, position: 'top' },
      tooltip: {
        callbacks: {
          label(context) {
            const v = context.parsed.y;
            return currencyUnit === 'KOBO'
              ? `${Number(v || 0).toLocaleString()} kobo`
              : `₦${Number(v || 0).toLocaleString()}`;
          },
        },
      },
      goalLine: { value: 0 },
    },
    scales: {
      x: {
        title: { display: true, text: 'Subscription Plan' },
        ticks: {
          autoSkip: false,
          callback(value) {
            return this.getLabelForValue(value);
          },
        },
      },
      y: {
        beginAtZero: true,
        max: planChartMax,
        title: { display: true, text: 'Revenue' },
        ticks: {
          callback(value) {
            return currencyUnit === 'KOBO'
              ? `${Number(value).toLocaleString()} kobo`
              : `₦${Number(value).toLocaleString()}`;
          },
        },
      },
    },
  };

  const chartMax = niceMax(revenueChartData.datasets[0].data);

  const revenueChartOptions = {
    responsive: true,
    plugins: {
      legend: { display: true, position: 'top' },
      tooltip: {
        callbacks: {
          label: function(context) {
            const v = context.parsed.y ?? context.parsed;
            return currencyUnit === 'KOBO' ? `${Number(v || 0).toLocaleString()} kobo` : `₦${Number(v || 0).toLocaleString()}`;
          }
        }
      }
    },
    scales: {
      y: {
        beginAtZero: true,
        max: chartMax,
        ticks: {
          callback: function(value) { return currencyUnit === 'KOBO' ? `${Number(value).toLocaleString()} kobo` : `₦${Number(value).toLocaleString()}`; }
        }
      }
    }
  };

  return (
    <Fragment>
      <div className="content-wrapper">
        <section className="content-header">
          <h1>
            Revenue Report
            <small>Revenue analytics and breakdown</small>
          </h1>
          <ol className="breadcrumb">
            <li>
              <Link to="/admin">
                <i className="fa fa-dashboard"></i> Dashboard
              </Link>
            </li>
            <li className="active">Reports</li>
            <li className="active">Revenue</li>
          </ol>
        </section>

        <section className="content">
          {/* Date Range Filter */}
          <div className="row">
            <div className="col-md-12">
              <div className="box box-info">
                <div className="box-header with-border">
                  <h3 className="box-title">Filter Report</h3>
                </div>
                <div className="box-body">
                  <div className="row">
                    <div className="col-md-4">
                      <div className="form-group">
                        <label>Date Range</label>
                        <select className="form-control" value={dateRange} onChange={(e) => setDateRange(e.target.value)}>
                          <option value="last7days">Last 7 Days</option>
                          <option value="last30days">Last 30 Days</option>
                          <option value="last90days">Last 90 Days</option>
                          <option value="ytd">Year to Date</option>
                          <option value="custom">Custom Range</option>
                        </select>
                      </div>
                    </div>

                    {dateRange === 'custom' && (
                      <>
                        <div className="col-md-4">
                          <div className="form-group">
                            <label>Start Date</label>
                            <input type="date" className="form-control" value={customStartDate} onChange={(e) => setCustomStartDate(e.target.value)} />
                          </div>
                        </div>
                        <div className="col-md-4">
                          <div className="form-group">
                            <label>End Date</label>
                            <input type="date" className="form-control" value={customEndDate} onChange={(e) => setCustomEndDate(e.target.value)} />
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>
            <div className="col-md-4" style={{display:'flex', alignItems:'center', justifyContent:'flex-end'}}>
              <CurrencyToggle onChange={(u) => setCurrencyUnit(u)} />
            </div>
          </div>

          {/* Summary Cards */}
          <div className="row">
            <div className="col-md-4">
              <div className="info-box">
                <span className="info-box-icon bg-yellow"><i className="fa fa-money"></i></span>
                <div className="info-box-content">
                  <span className="info-box-text">Total Revenue</span>
                  <span className="info-box-number">{loading ? <Loader /> : `₦${Number(revenueReport?.totalRevenue || 0).toLocaleString()}`}</span>
                </div>
              </div>
            </div>
            <div className="col-md-4">
              <div className="info-box">
                <span className="info-box-icon bg-red"><i className="fa fa-pie-chart"></i></span>
                <div className="info-box-content">
                  <span className="info-box-text">Avg Revenue/Subscription</span>
                  <span className="info-box-number">{loading ? <Loader /> : `₦${Number(revenueReport?.average || 0).toLocaleString()}`}</span>
                </div>
              </div>
            </div>
            <div className="col-md-4">
              <div className="info-box">
                <span className="info-box-icon bg-blue"><i className="fa fa-chart-line"></i></span>
                <div className="info-box-content">
                  <span className="info-box-text">Data Points</span>
                  <span className="info-box-number">{(revenueOverTime || []).length}</span>
                </div>
              </div>
            </div>
          </div>
          <div className="box box-primary">
            <div className="box-header with-border">
              <h3 className="box-title">Revenue Overview</h3>
              <div style={{float:'right'}}>
                <button className="btn btn-default btn-sm" onClick={() => downloadCSV('revenue_report.csv', revenueOverTime.map(r => ({ date: r.date, revenue: (currencyUnit === 'KOBO' ? Number(r.revenue) * 100 : Number(r.revenue)) })) ) }>Export CSV</button>
              </div>
            </div>
            <div className="box-body">
              {loading ? <Loader /> : (
                <>
                  <div style={{height:420}}>
                    {revenueChartData.datasets[0].data.length === 0 || revenueChartData.datasets[0].data.every(v => v === 0) ? (
                      <p className="text-center">No revenue data available for the selected range.</p>
                    ) : (
                      <Bar data={revenueChartData} options={{...revenueChartOptions, maintainAspectRatio:false}} />
                    )}
                  </div>
                  <h4 className="mt-3">Revenue by Plan</h4>
                  <div style={{height:360}}>
                    {revenueByPlanData.datasets[0].data.length === 0 || revenueByPlanData.datasets[0].data.every(v => v === 0) ? (
                      <p className="text-center">No revenue-by-plan data available for the selected range.</p>
                    ) : (
                      <Bar
                        data={revenueByPlanData}
                        options={revenueByPlanChartOptions}
                      />
                    )}
                  </div>
                </>
              )}
            </div>
          </div>
        </section>
      </div>
    </Fragment>
  );
};

export default RevenueReport;
